import React from 'react';
import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom';

import { BodySmall, Box, Button, H6, Select, Snackbar } from '@parspec/pixel';

import { DEFAULT_PRECISION, NO_ROUNDING, ROUNDING_BEHAVIOUR, ROUNDING_PRECISION } from 'src/features/BOM/shared/constants';
import { useLocationDetails } from '../context';
import { useParspecNavigation } from 'src/app/Context';
import { SaveButton } from '../../shared/components/SaveButton';
import { EDIT_SETTINGS_SUCCESS_MSG } from '../../shared/constants';
import { MultipleBranchesModal } from '../../shared/components/MultipleBranchesModal/MultipleBranchesModal';
import { useEditBomSettingsMutation } from '../../queries';



const BomSettings = () => {

    const { currentLocationId, setBomSettingTemplate, bomSettingTemplate, branchLocationsRef } = useLocationDetails();

    const { pNavigate, hasUnsavedChangesRef } = useParspecNavigation()
    const location = useLocation();
    const currentTab = location.pathname.split('/').filter(Boolean).pop();

    const { mutateAsync: editBomSettingsData, isLoading } = useEditBomSettingsMutation();


    const [isMultipleBranchesModalOpen, setIsMultipleBranchesModalOpen] = useState(false);
    const [snackbarData, setSnackbarData] = useState('');


    useEffect(() => {
        const currentBomSettings = branchLocationsRef.current;
        return () => {
            if (currentBomSettings?.bomSettingTemplate) {
                setBomSettingTemplate(currentBomSettings.bomSettingTemplate)
            }
        }
    }, [branchLocationsRef, setBomSettingTemplate])


    const handleRoundingPrecision = (event: React.ChangeEvent<HTMLInputElement>) => {
        hasUnsavedChangesRef.current = true;
        setBomSettingTemplate({ ...bomSettingTemplate, rounding_precision: event.target.value });
    };

    const handleRoundingBehviour = (event: React.ChangeEvent<HTMLInputElement>) => {
        hasUnsavedChangesRef.current = true;
        const precision = bomSettingTemplate.rounding_precision === NO_ROUNDING ? DEFAULT_PRECISION : bomSettingTemplate.rounding_precision;
        setBomSettingTemplate({ ...bomSettingTemplate, rounding_behaviour: event.target.value, rounding_precision: precision });
    };

    const resetDataHandler = () => {
        pNavigate(`/v2/settings/company-locations`);
    };

    const preparePayloadHandler = (locationArr: number[]) => {
        const payloadData = {
            apply_settings_to_company_ids: locationArr,
            payload: {
                rounding_behaviour: bomSettingTemplate?.rounding_behaviour,
                rounding_precision: bomSettingTemplate?.rounding_behaviour === NO_ROUNDING ? NO_ROUNDING : bomSettingTemplate.rounding_precision
            }
        }
        return payloadData
    }

    const onOptionSelection = async (type: string) => {
        switch (type) {
            case 'single':
                const payloadData = preparePayloadHandler([currentLocationId])
                try {
                    hasUnsavedChangesRef.current = false;
                    await editBomSettingsData({ data: payloadData });
                    setSnackbarData(() => EDIT_SETTINGS_SUCCESS_MSG)
                } catch (err: any) {
                    setSnackbarData(() => err?.response?.data?.message ? err?.response?.data?.message : 'Something went wrong, please try again');
                }
                break;

            case 'multiple':
                setIsMultipleBranchesModalOpen(() => true);
                break;
        }
    };

    const onColumnSelection = async (selectedBranchIdsArr: number[]) => {

        const payloadData = preparePayloadHandler(selectedBranchIdsArr)

        try {
            hasUnsavedChangesRef.current = false;
            await editBomSettingsData({ data: payloadData });
            setSnackbarData(() => EDIT_SETTINGS_SUCCESS_MSG)
        } catch (err: any) {
            setSnackbarData(() => err?.response?.data?.message ? err?.response?.data?.message : 'Something went wrong, please try again');
        }
        setIsMultipleBranchesModalOpen(() => false);
    };

    const handleCloseMultipleBranchesModal = () => {
        setIsMultipleBranchesModalOpen(() => false);
    }

    return (
        <Box bgcolor="primary.contrastText" min-width={'1000px'}>

            <Box display="flex">
                <Box>
                    <H6>Rounding</H6>
                </Box>
                <Box ml="auto" display="flex">
                    <Box display="flex">
                        <Box mr={2}>
                            <Button variant="outlined" color="secondary" onClick={resetDataHandler}>
                                Cancel
                            </Button>
                        </Box>
                        <Box>
                            <SaveButton onOptionSelection={onOptionSelection} isLoading={isLoading} />
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Box mt={6}>
                <BodySmall limit={false} color="text.secondary" mb={4}>
                    Select the default rounding behavior for bills of materials. The margin (%) will automatically recalculate to meet the set behavior.
                </BodySmall>
                <Box display={'flex'} gap={4}>
                    <Box width={250}>
                        <Select
                            label={'Rounding Behavior'}
                            options={ROUNDING_BEHAVIOUR}
                            optionLabelKeyname="name"
                            optionValueKeyname="name"
                            onChange={handleRoundingBehviour}
                            value={bomSettingTemplate.rounding_behaviour}
                        />
                    </Box>
                    <Box width={250}>
                        <Select
                            label={'Rounding Precision'}
                            options={ROUNDING_PRECISION}
                            optionLabelKeyname="name"
                            optionValueKeyname="id"
                            onChange={handleRoundingPrecision}
                            value={bomSettingTemplate.rounding_behaviour === NO_ROUNDING ? '' : bomSettingTemplate.rounding_precision}
                            disabled={bomSettingTemplate.rounding_behaviour === NO_ROUNDING}
                        />
                    </Box>
                </Box>
            </Box>

            <MultipleBranchesModal
                isOpen={isMultipleBranchesModalOpen}
                onClose={handleCloseMultipleBranchesModal}
                currentTab={currentTab}
                onColumnSelection={onColumnSelection}
                isLoading={isLoading}

            />

            {<Snackbar open={snackbarData?.length > 0} message={snackbarData} onClose={() => setSnackbarData(() => '')} />}
        </Box>

    );
};
export default React.memo(BomSettings);
